<div class="gl-page">
  <!-- Back link -->
  <yds-button-back text="Ga terug 'Overzicht grootboekrekening'" (back)="goBack()" />

  <!-- Title + page actions -->
  <div class="gl-page__header">
    <h1 class="gl-page__title">Grootboekrekening</h1>
    <div class="gl-page__header-actions">
      <yds-button (click)="goToGeneralLedger()">Grootboek</yds-button>
      <yds-button (click)="goToGeneralLedgerGroup()">Grootboekrekeningengroep</yds-button>
      @if (!isAddMode()) {
        <yds-button variant="destructive" (click)="onDelete()">Verwijderen</yds-button>
      }
    </div>
  </div>

  @if (isLoading()) {
    <div class="gl-detail-loading">
      <yds-spinner [label]="'Grootboekrekening laden...'" />
    </div>
  } @else if (notFound()) {
    <p class="bge-empty">Er zijn geen details voor grootboekrekening gevonden.</p>
  } @else {
    <!-- Details card -->
    <yds-card-editable
      #detailsCard
      title="Details"
      [(editing)]="detailsEditing"
      [nextDisabled]="isSaving() || form.invalid"
      [nextLabel]="isAddMode() ? 'Volgende' : 'Opslaan'"
      (cancel)="onDetailsCancel()"
      (next)="onDetailsNext()"
    >
      @if (detailsEditing()) {
        <form class="gl-form" [formGroup]="form">
          <p class="gl-form__required-hint">
            Velden gemarkeerd met een sterretje
            (<span class="gl-form__required-hint-asterisk" aria-hidden="true">*</span>) zijn verplicht.
          </p>

          <div class="gl-field">
            <label class="gl-field__label">
             <span class="gl-form__required-hint-asterisk" aria-hidden="true">*</span> Nummer 
            </label>
            <div class="gl-field__control">
              <yds-text-field
                formControlName="generalLedgerAccount"
                [required]="true"
                [hasError]="isInvalid(form.get('generalLedgerAccount'))"
                [errorMsg]="'Nummer is verplicht.'"
                [placeholder]="'Voer een nummer in'"
              />
            </div>
          </div>

          <div class="gl-field">
            <label class="gl-field__label">
             <span class="gl-form__required-hint-asterisk" aria-hidden="true">*</span> Omschrijving 
            </label>
            <div class="gl-field__control">
              <yds-text-field
                formControlName="generalLedgerAccountName"
                [required]="true"
                [hasError]="isInvalid(form.get('generalLedgerAccountName'))"
                [errorMsg]="'Omschrijving is verplicht.'"
                [placeholder]="'Voer een omschrijving in'"
              />
            </div>
          </div>

          <div class="gl-field">
            <label class="gl-field__label">Grootboek</label>
            <div class="gl-field__control">
              <span class="gl-detail__value">{{ generalLedgerName() }}</span>
            </div>
          </div>

          <div class="gl-field">
            <label class="gl-field__label">
              Grootboekrekeningengroep
            </label>
            <div class="gl-field__control">
              <yds-dropdown
                formControlName="generalLedgerGroupId"
                [items]="generalLedgerGroupOptions()"
                [allowClear]="true"
                [placeholder]="'Selecteer een grootboekrekeningengroep'"
              />
            </div>
          </div>

          <div class="gl-field">
            <label class="gl-field__label">
             <span class="gl-form__required-hint-asterisk" aria-hidden="true">*</span> Soort rekening 
            </label>
            <div class="gl-field__control">
              <yds-dropdown
                formControlName="kindOfAccount"
                [items]="kindOfAccountOptions"
                [required]="true"
                [allowClear]="false"
                [placeholder]="'Selecteer een soort rekening'"
              />
            </div>
          </div>

          <div class="gl-field gl-field--inline">
            <label class="gl-field__label">Boeken negatieve waarden</label>
            <div class="gl-field__control">
              <yds-switch formControlName="bookNegativeValues" />
            </div>
          </div>

          <div class="gl-field gl-field--inline">
            <label class="gl-field__label">Boeken per persoon</label>
            <div class="gl-field__control">
              <yds-switch formControlName="bookByEmployment" />
            </div>
          </div>

          <div class="gl-field">
            <label class="gl-field__label">
             <span class="gl-form__required-hint-asterisk" aria-hidden="true">*</span> Begindatum 
            </label>
            <div class="gl-field__control">
              <yds-date-picker
                formControlName="startDate"
                [required]="true"
                [locale]="translate.getCurrentLang()"
                [placeholder]="'dd-mm-jjjj'"
                [allowTodayAction]="false"
                [dateFilter]="startDateFilter"
                [dateFilterError]="'Alleen de eerste dag van de maand is toegestaan.'"
                [errorMessage]="
                  isInvalid(form.get('startDate'))
                    ? 'Begindatum is verplicht.'
                    : form.hasError('startBeforeEnd') && form.get('startDate')?.touched
                      ? 'Begindatum moet voor einddatum liggen'
                      : ''
                "
              />
            </div>
          </div>

          <div class="gl-field">
            <label class="gl-field__label">Einddatum</label>
            <div class="gl-field__control">
              <yds-date-picker
                formControlName="endDate"
                [locale]="translate.getCurrentLang()"
                [placeholderKey]="null"
                [placeholder]="''"
                [allowTodayAction]="false"
                [dateFilter]="endDateFilter"
                [dateFilterError]="'Alleen de laatste dag van de maand is toegestaan.'"
                [errorMessage]="
                  form.hasError('startBeforeEnd') && form.get('endDate')?.touched
                    ? 'Begindatum moet voor einddatum liggen'
                    : ''
                "
              />
            </div>
          </div>

          <div class="gl-field">
            <label class="gl-field__label">Memo</label>
            <div class="gl-field__control">
              <yds-text-area
                formControlName="memoValue"
                [placeholder]="'Voer een memo in'"
                [maxLength]="500"
                [showCounter]="true"
              />
            </div>
          </div>
        </form>
      } @else {
        <div class="gl-detail">
          <div class="gl-detail__row">
            <span class="gl-detail__label">Nummer</span>
            <span class="gl-detail__value">{{ accountNumber() }}</span>
          </div>
          <div class="gl-detail__row">
            <span class="gl-detail__label">Omschrijving</span>
            <span class="gl-detail__value">{{ accountName() }}</span>
          </div>
          <div class="gl-detail__row">
            <span class="gl-detail__label">Grootboek</span>
            <span class="gl-detail__value">{{ generalLedgerName() }}</span>
          </div>
          <div class="gl-detail__row">
            <span class="gl-detail__label">Grootboekrekeningengroep</span>
            <span class="gl-detail__value">{{ generalLedgerGroupDisplay() }}</span>
          </div>
          <div class="gl-detail__row">
            <span class="gl-detail__label">Soort rekening</span>
            <span class="gl-detail__value">{{ kindOfAccountDisplay() }}</span>
          </div>
          <div class="gl-detail__row gl-detail__row--inline">
            <span class="gl-detail__label">Boeken negatieve waarden</span>
            <span class="gl-detail__value gl-detail__value--setting">
              @if (bookNegativeValuesOn()) {
                <span class="vismaicon vismaicon-dynamic vismaicon-sm vismaicon-checkmark glga-checkmark" aria-hidden="true"></span>
                <span>Aan</span>
              } @else {
                <span>Uit</span>
              }
            </span>
          </div>
          <div class="gl-detail__row gl-detail__row--inline">
            <span class="gl-detail__label">Boeken per persoon</span>
            <span class="gl-detail__value gl-detail__value--setting">
              @if (bookByEmploymentOn()) {
                <span class="vismaicon vismaicon-dynamic vismaicon-sm vismaicon-checkmark glga-checkmark" aria-hidden="true"></span>
                <span>Aan</span>
              } @else {
                <span>Uit</span>
              }
            </span>
          </div>
          <div class="gl-detail__row">
            <span class="gl-detail__label">Begindatum</span>
            <span class="gl-detail__value">{{ startDateDisplay() }}</span>
          </div>
          <div class="gl-detail__row">
            <span class="gl-detail__label">Einddatum</span>
            <span class="gl-detail__value">{{ endDateDisplay() }}</span>
          </div>
          <div class="gl-detail__row">
            <span class="gl-detail__label">Memo</span>
            <span class="gl-detail__value gl-detail__value--memo">{{ memoDisplay() }}</span>
          </div>
        </div>
      }
    </yds-card-editable>

    @if (!isAddMode()) {
      <!-- Elementen boekingsomschrijving card -->
      <yds-card-editable
        class="bgc-criteria-card"
        title="Elementen boekingsomschrijving"
        [(editing)]="displayCriteriaEditing"
        nextLabel="Opslaan"
        [nextDisabled]="isSavingDisplayCriteria()"
        (cancel)="onDisplayCriteriaCancel()"
        (next)="onDisplayCriteriaSave()"
      >
        @if (displayCriteriaEditing()) {
          @if (criteriaOptions().length === 0) {
            <p class="bge-empty">Geen boekingsomschrijvingen beschikbaar.</p>
          } @else {
            <div class="glga-criteria-list">
              @for (option of criteriaOptions(); track option.number) {
                <yds-checkbox
                  [label]="option.description"
                  [ngModel]="isCriterionChecked(option.number)"
                  [ngModelOptions]="{ standalone: true }"
                  (ngModelChange)="toggleCriterion(option.number, $event)"
                />
              }
            </div>
          }
        } @else {
          @if (selectedCriteriaLabels().length === 0) {
            <p class="bge-empty">Nog geen boekingsomschrijving geselecteerd.</p>
          } @else {
            <div class="gl-detail">
              @for (label of selectedCriteriaLabels(); track label) {
                <div class="gl-detail__row gl-detail__row--inline">
                  <span class="gl-detail__label">{{ label }}</span>
                  <span class="gl-detail__value gl-detail__value--setting">
                    <span class="vismaicon vismaicon-dynamic vismaicon-sm vismaicon-checkmark glga-checkmark" aria-hidden="true"></span>
                    <span>Aan</span>
                  </span>
                </div>
              }
            </div>
          }
        }
      </yds-card-editable>

      <!-- Variabel rekeningnummer card -->
      <yds-card-editable
        class="bgc-criteria-card"
        title="Variabel rekeningnummer"
        [(editing)]="variableEditing"
        nextLabel="Opslaan"
        [nextDisabled]="isSavingVariable()"
        (cancel)="onVariableCancel()"
        (next)="onVariableSave()"
      >
        @if (variableEditing()) {
          <div class="gl-grid-wrapper">
            <table class="gl-grid glga-var-grid">
              <thead>
                <tr>
                  <th class="glga-var-grid__col--element">Element</th>
                  <th class="glga-var-grid__col--pos">Beginpositie</th>
                  <th class="glga-var-grid__col--pos">Lengte</th>
                  <th class="glga-var-grid__col--value">Vaste waarde</th>
                  <th class="gl-grid__col--action"></th>
                </tr>
              </thead>
              <tbody>
                @if (variableRows().length === 0) {
                  <tr class="gl-grid__empty">
                    <td colspan="5">Nog geen variabel rekeningnummer toegevoegd.</td>
                  </tr>
                } @else {
                  @for (row of variableRows(); track $index) {
                    <tr>
                      <td class="glga-var-grid__col--element">
                        <yds-combobox
                          [items]="elementOptions()"
                          [ngModel]="row.elementId"
                          [ngModelOptions]="{ standalone: true }"
                          (ngModelChange)="updateVariableRow($index, { elementId: $event ?? '' })"
                          [allowClear]="true"
                          [placeholder]="'Selecteer een element'"
                        />
                      </td>
                      <td class="glga-var-grid__col--pos">
                        <yds-text-field
                          [type]="'number'"
                          [ngModel]="row.startPosition"
                          [ngModelOptions]="{ standalone: true }"
                          (ngModelChange)="onStartPositionChange($index, $event)"
                          [placeholder]="'Vul een beginpositie in'"
                        />
                      </td>
                      <td class="glga-var-grid__col--pos">
                        <yds-text-field
                          [type]="'number'"
                          [ngModel]="row.numberOfPositions"
                          [ngModelOptions]="{ standalone: true }"
                          (ngModelChange)="onNumberOfPositionsChange($index, $event)"
                          [placeholder]="'Vul een lengte in'"
                        />
                      </td>
                      <td class="glga-var-grid__col--value">
                        <yds-text-field
                          [ngModel]="row.fixedValue"
                          [ngModelOptions]="{ standalone: true }"
                          (ngModelChange)="updateVariableRow($index, { fixedValue: $event ?? '' })"
                          [maxLength]="fixedValueMaxLength"
                          [placeholder]="'Vul een waarde in'"
                        />
                      </td>
                      <td class="gl-grid__col--action">
                        <button
                          type="button"
                          class="gl-grid__remove"
                          aria-label="Verwijder variabel rekeningnummer"
                          (click)="removeVariableRow($index)"
                        >
                          <span class="vismaicon vismaicon-dynamic vismaicon-sm vismaicon-delete" aria-hidden="true"></span>
                        </button>
                      </td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>

          <button
            type="button"
            class="gl-grid__add-link"
            (click)="addVariableRow()"
            [disabled]="!canAddVariableRow()"
          >
            + Variabel rekeningnummer toevoegen
          </button>
          @if (!canAddVariableRow()) {
            <p class="glga-var-grid__hint">Maximaal {{ maxVariableRows }} rijen.</p>
          }
        } @else {
          @if (variableElements().length === 0) {
            <p class="bge-empty">Nog geen variabel rekeningnummer toegevoegd.</p>
          } @else {
            <div class="gl-grid-wrapper">
              <table class="gl-grid glga-var-grid">
                <thead>
                  <tr>
                    <th class="glga-var-grid__col--element">Element</th>
                    <th class="glga-var-grid__col--pos">Beginpositie</th>
                    <th class="glga-var-grid__col--pos">Lengte</th>
                    <th class="glga-var-grid__col--value">Vaste waarde</th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of variableElements(); track $index) {
                    <tr>
                      <td class="glga-var-grid__col--element">{{ variableElementLabel(row.elementId) }}</td>
                      <td class="glga-var-grid__col--pos">{{ row.startPosition ?? 'n.v.t.' }}</td>
                      <td class="glga-var-grid__col--pos">{{ row.numberOfPositions ?? 'n.v.t.' }}</td>
                      <td class="glga-var-grid__col--value">{{ row.fixedValue || 'n.v.t.' }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        }
      </yds-card-editable>
    }
  }
</div>
